import {
    CheckCircleIcon,
    ClockIcon,
    ExclamationCircleIcon,
    MinusCircleIcon,
} from "@heroicons/react/20/solid";
import { BusinessVerificationStatus } from "@sp-crm/core";
import { InternalLink } from "components/ui/internal-link";
import React from "react";
import { businessProfilePath } from "./business-profile-types";
import {
    callingStatusOf,
    customerBusinessStyles,
    CustomerStatusStyle,
    customerTextingRejection,
    CustomerTrustProduct,
    customerTrustProductDescriptions,
    customerTrustProductStyles,
    StatusTone,
} from "./calling-status";
import { BusinessProfileOutput } from "./use-business-verification";

const toneIcons: Record<StatusTone, typeof CheckCircleIcon> = {
    ok: CheckCircleIcon,
    info: ClockIcon,
    bad: ExclamationCircleIcon,
    idle: MinusCircleIcon,
};

const toneIconColors: Record<StatusTone, string> = {
    ok: "text-green-600",
    info: "text-brand-500",
    bad: "text-red-600",
    idle: "text-gray-400",
};

const toneTextColors: Record<StatusTone, string> = {
    ok: "text-green-700",
    info: "text-brand-700",
    bad: "text-red-700",
    idle: "text-gray-500",
};

export const StatusIcon: React.FC<{ tone: StatusTone; sizeClass: string }> = ({
    tone,
    sizeClass,
}) => {
    const Icon = toneIcons[tone];
    return (
        <Icon
            aria-hidden="true"
            className={`${sizeClass} shrink-0 ${toneIconColors[tone]}`}
        />
    );
};

export const StatusLabel: React.FC<{ style: CustomerStatusStyle }> = ({ style }) => (
    <span className={`inline-flex items-center gap-1.5 ${toneTextColors[style.tone]}`}>
        <StatusIcon tone={style.tone} sizeClass="size-4" />
        {style.label}
    </span>
);

const StatusText: React.FC<{ style: CustomerStatusStyle }> = ({ style }) => (
    <span className={`text-sm font-medium ${toneTextColors[style.tone]}`}>
        {style.label}
    </span>
);

interface StatusPanelHeaderProps {
    title: string;
    what: string;
    style: CustomerStatusStyle;
}

const panelClasses = "rounded-md border border-gray-200 p-3 space-y-2";

const StatusPanelHeader: React.FC<StatusPanelHeaderProps> = ({ title, what, style }) => (
    <div className="flex items-start gap-2">
        <span className="flex h-6 shrink-0 items-center">
            <StatusIcon tone={style.tone} sizeClass="size-5" />
        </span>
        <div className="min-w-0 flex-1">
            <div className="flex flex-wrap items-baseline justify-between gap-x-3">
                <span className="font-medium text-gray-900">{title}</span>
                <StatusText style={style} />
            </div>
            <p className="text-sm text-gray-600">{what}</p>
        </div>
    </div>
);

const StatusPanel: React.FC<StatusPanelHeaderProps & { children: React.ReactNode }> = ({
    children,
    ...header
}) => (
    <section className={panelClasses}>
        <StatusPanelHeader {...header} />
        {children}
    </section>
);

const callingDescriptions: Record<BusinessVerificationStatus, string> = {
    draft: "Calls are registered once your business is verified",
    inReview: "Calls are registered once your business is verified",
    needsAttention: "Calls are registered once your business is verified",
    verified: "Calls from your numbers are registered with carriers",
};

const textingDescriptions: Record<BusinessVerificationStatus, string> = {
    draft: "Texts are registered once your business is verified",
    inReview: "Texts are registered once your business is verified",
    needsAttention: "Texts are registered once your business is verified",
    verified: "Texts from your numbers are registered with carriers",
};

export const textingAwaitingApproval =
    "Your numbers can send texts once carriers approve your texting registration";

const textingDescriptionOf = (
    profile: Pick<BusinessProfileOutput, "verificationStatus" | "textingStatus">,
): string =>
    profile.verificationStatus === "verified" && profile.textingStatus !== "active"
        ? textingAwaitingApproval
        : textingDescriptions[profile.verificationStatus];

const TrustProductItem: React.FC<{ product: CustomerTrustProduct }> = ({ product }) => {
    const description = customerTrustProductDescriptions[product.type];
    const style = customerTrustProductStyles[product.status];
    return (
        <li className="py-2 space-y-1.5">
            <div className="flex items-start gap-2">
                <span className="flex h-5 shrink-0 items-center">
                    <StatusIcon tone={style.tone} sizeClass="size-4" />
                </span>
                <div className="min-w-0 flex-1">
                    <div className="flex flex-wrap items-baseline justify-between gap-x-3">
                        <span className="text-sm text-gray-900">{description.name}</span>
                        <StatusText style={style} />
                    </div>
                    <p className="text-sm text-gray-600">{description.what}</p>
                </div>
            </div>
            {product.status === "needsAttention" ? (
                <p className="ml-6 rounded bg-red-50 px-2 py-1.5 text-sm text-red-800">
                    {description.rejection}
                </p>
            ) : null}
        </li>
    );
};

const CallingProducts: React.FC<{ profile: BusinessProfileOutput }> = ({ profile }) =>
    profile.verificationStatus === "verified" ? (
        <ul className="ml-7 divide-y divide-gray-100">
            {profile.trustProducts.map(product => (
                <TrustProductItem key={product.type} product={product} />
            ))}
        </ul>
    ) : null;

export const BusinessStatusPanels: React.FC<{ profile: BusinessProfileOutput }> = ({
    profile,
}) => (
    <div className="space-y-3">
        <StatusPanel
            title="Business identity"
            what="Carriers confirm who your business is"
            style={customerBusinessStyles[profile.verificationStatus]}>
            <InternalLink className="ml-7 text-sm" to={businessProfilePath(profile.id)}>
                See business details
            </InternalLink>
        </StatusPanel>
        <StatusPanel
            title="Texting"
            what={textingDescriptionOf(profile)}
            style={customerTrustProductStyles[profile.textingStatus]}>
            {profile.textingStatus === "needsAttention" ? (
                <p className="ml-7 rounded bg-red-50 px-2 py-1.5 text-sm text-red-800">
                    {customerTextingRejection}
                </p>
            ) : null}
        </StatusPanel>
        <StatusPanel
            title="Calling"
            what={callingDescriptions[profile.verificationStatus]}
            style={customerTrustProductStyles[callingStatusOf(profile.trustProducts)]}>
            <CallingProducts profile={profile} />
        </StatusPanel>
    </div>
);
